// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

import * as FilestackUrls from "@outschool/filestack-urls";
import {
  ActivityDetails,
  Activity as ActivityType,
  Leader,
  Section
} from "@outschool/gql-backend-generated";
import {
  ListingsByActivityUidsQuery as ListingsByActivityUidsQueryType,
  ListingsByActivityUidsQueryVariables
} from "@outschool/gql-frontend-generated";
import { renderCurrencyString, useLocale } from "@outschool/localization";
import { leaderPath, pathToUrl } from "@outschool/routes";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { ListingsByActivityUidsQuery } from "@outschool/ui-components-website";
import React from "react";

import * as Activity from "../../shared/Activity";
import * as Routes from "../../shared/Routes";

type CourseMetadataProps = {
  activity: Pick<
    ActivityType,
    | "uid"
    | "title"
    | "summary"
    | "published_at"
    | "slug_id"
    | "isClub"
    | "details"
    | "leader"
    | "averageStarRating"
    | "reviewCount"
    | "price_cents"
    | "duration_weeks"
    | "weekly_meetings"
    | "is_ongoing_weekly"
    | "completedEnrollmentCount"
  > & {
    details: Pick<ActivityDetails, "photo">;
  } & { leader: Pick<Leader, "name"> };
  section: Pick<Section, "uid">;
};

export function useCourseMetadata({ activity, section }: CourseMetadataProps) {
  const locale = useLocale();

  return React.useMemo(() => {
    const imageUrl = FilestackUrls.imageUrl(activity.details.photo, {
      w: Activity.MAIN_IMAGE_WIDTH * 2,
      h: Activity.MAIN_IMAGE_HEIGHT * 2,
      fit: "crop"
    });

    const price = renderCurrencyString({
      priceInCents: activity.price_cents ?? 0,
      showCurrencyCode: true,
      locale: locale
    })
      .slice(1)
      .split(" ");

    const courseData = {
      "@context": "https://schema.org",
      "@type": "Course",
      name: activity.title,
      description: activity.summary,
      thumbnailUrl: imageUrl,
      datePublished: activity.published_at,
      url: Routes.sectionOrActivityUrl(activity, section),
      provider: {
        "@type": "Person",
        name: activity.leader ? activity.leader.name : "Outschool Teacher"
      },
      totalHistoricalEnrollment: activity?.completedEnrollmentCount ?? 0,
      offers: {
        "@type": "Offer",
        category:
          activity?.price_cents && activity.price_cents > 0 ? "Paid" : "Free",
        price: price[0],
        priceCurrency:
          activity?.price_cents && activity.price_cents > 0
            ? price[1]
            : undefined
      },
      hasCourseInstance: [
        {
          "@type": "CourseInstance",
          courseMode: "Online",
          courseWorkLoad: activity.is_ongoing_weekly
            ? "P" + activity.duration_weeks + "W"
            : "P1H",
          courseSchedule: activity.is_ongoing_weekly
            ? {
                "@type": "Schedule",
                repeatCount: activity.weekly_meetings ?? undefined,
                repeatFrequency: activity.is_ongoing_weekly
                  ? "Weekly"
                  : undefined
              }
            : undefined,
          instructor: [
            {
              "@type": "Person",
              name: activity.leader
                ? activity.leader.name
                : "Outschool Teacher",
              description: activity?.leader?.details?.headlineTranslated,
              url: pathToUrl(
                leaderPath(activity?.leader?.uid, activity?.leader?.leader_link)
              )
            }
          ]
        }
      ]
    };

    let reviewData;
    let aggregateRating;
    const ratingValue =
      !!activity?.averageStarRating && activity?.averageStarRating > 0
        ? parseFloat(activity?.averageStarRating.toFixed(2))
        : 0;
    const ratingCount = activity?.reviewCount ?? 0;

    if (ratingValue > 0 && ratingCount > 0) {
      aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: ratingValue.toString(),
        ratingCount: ratingCount.toString(),
        reviewCount: ratingCount.toString()
      };

      reviewData = {
        "@context": "https://schema.org/",
        ...aggregateRating,
        bestRating: "5",
        worstRating: "1",
        itemReviewed: {
          ...courseData
        }
      };
    }

    // if review data exists then return an array
    const metadataRollup = !!reviewData ? [reviewData, courseData] : courseData;
    return {
      type: "application/ld+json",
      innerHTML: JSON.stringify(metadataRollup, undefined, 2)
    };
  }, [activity, section, locale]);
}

export function useListingActivitiesByUid(
  activityUids: string[],
  skipRating = true,
  skip = false
) {
  const { data, loading } = useQueryWithPreviousData<
    ListingsByActivityUidsQueryType,
    ListingsByActivityUidsQueryVariables
  >(ListingsByActivityUidsQuery, {
    skip: skip || !activityUids?.length,
    variables: {
      skipRating,
      withRecentReviews: false,
      activityUids
    }
  });
  return {
    loading,
    listings: data?.listingsByActivityUids || []
  };
}
